import React, { type ReactNode } from 'react';
import { Box, Drawer, type DrawerProps, IconButton, Typography, useTheme } from '@mui/material';
import { useBDrawerStyles } from './useBDrawerStyles';
import { CloseOutlined } from '@mui/icons-material';

export interface BDrawerProps extends Omit<DrawerProps, 'children' | 'title'> {
  children: ReactNode;
  showCloseButton?: boolean;
  showTopBar?: boolean;
  showBottomBar?: boolean;
  title?: ReactNode;
  dialogActions?: ReactNode;
}

const BDrawer: React.FC<BDrawerProps> = ({
  children,
  showCloseButton = true,
  showTopBar = true,
  showBottomBar = true,
  title,
  onClose,
  anchor = 'bottom',
  dialogActions,
  ...drawerProps
}) => {
  const theme = useTheme();
  const styles = useBDrawerStyles();

  const handleCloseClick = () => {
    if (onClose) {
      onClose({}, 'backdropClick');
    }
  };

  return (
    <Drawer
      {...drawerProps}
      anchor={anchor}
      onClose={onClose}
      PaperProps={{
        ...drawerProps.PaperProps,
        sx: [
          styles.drawerPaper,
          ...(drawerProps.PaperProps?.sx
            ? Array.isArray(drawerProps.PaperProps.sx)
              ? drawerProps.PaperProps.sx
              : [drawerProps.PaperProps.sx]
            : [])
        ]
      }}
    >
      {showTopBar && (
        <Box sx={styles.topBar}>
          <Box sx={styles.indicator} />
        </Box>
      )}
      {/* Top bar with indicator and close button */}

      <Box sx={title ? styles.headerContainer : styles.headerContainerCentered}>
        {/* Title on left */}
        <Typography variant='h4' color='text.primary'>
          {title}
        </Typography>

        {/* Close button on right */}
        {showCloseButton && (
          <IconButton
            onClick={handleCloseClick}
            sx={styles.closeButton}
            size="Xs"
          >
            <CloseOutlined color="inherit" />
          </IconButton>
        )}
      </Box>

      {/* Content */}
      <Box sx={showBottomBar ? styles.contentContainer : styles.contentWithBottomPadding}>
        {children}
      </Box>
      {dialogActions && (
        <Box
          sx={{
            width: '100%',
            padding: theme.spacing(1),
            display: 'flex',
            justifyContent: 'flex-end',
            gap: theme.spacing(1.5)
          }}
        >
          {dialogActions}
        </Box>
      )}
    </Drawer>
  );
};

export default BDrawer;
